// components/tasks/TaskBoardView.tsx
// Pull-based kanban board for the task tracker. Columns:
//   待辦(todo) → 進行中(doing) → 等待中(waiting) → 已完成(completed-derived).
// The three active columns map to the backend `board_section` values; 已完成 is
// DERIVED from status==='completed' (never a section). Cards are moved between
// columns with NATIVE HTML5 drag-and-drop (no dnd library): draggable cards +
// onDragStart/onDragOver/onDrop with a visual drop highlight. The parent owns
// the tasks state and performs the optimistic API calls; this view owns the DnD.
import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Checkbox,
  Button,
  Tooltip,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Close as CloseIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { Task, Employee } from './TaskDetailDrawer';
import { recurrenceLabel, nextOccurrence, daysUntil } from './recurrence';

// The three active pull-based columns in display order. Keys match the backend
// contract ('todo' | 'doing' | 'waiting'); 已完成 is derived, not a section.
export const BOARD_SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'todo', label: '待辦' },
  { key: 'doing', label: '進行中' },
  { key: 'waiting', label: '等待中' },
  { key: 'review', label: '待審核' },
];

const COMPLETED_KEY = 'completed';
// 週期 is a DERIVED column, like 已完成: membership comes from the card carrying a
// recurrence rule, not from board_section. So a routine lives in its own lane and
// never clutters 待辦. Dragging a card in makes it recur (default 每週); dragging
// it out to an active column clears the rule.
const RECURRING_KEY = 'recurring';
const DEFAULT_RECURRENCE = 'every:1w';

const priorityLabels: Record<string, string> = { low: '低', medium: '中', high: '高' };
const priorityColors: Record<string, 'success' | 'warning' | 'error'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};
// 4px left-border accent per priority (high=red / medium=amber / low=green).
const priorityBorderColors: Record<string, string> = {
  low: 'success.main',
  medium: 'warning.main',
  high: 'error.main',
};
const priorityRank: Record<string, number> = { high: 0, medium: 1, low: 2 };

// Normalize a possibly-missing/legacy board_section to a valid active column key.
// Any unknown/legacy value (e.g. old recent/today/next_week/later) falls to 待辦.
const sectionOf = (task: Task): string => {
  const s = task.board_section;
  return s && BOARD_SECTIONS.some((sec) => sec.key === s) ? s : 'todo';
};

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDueDate = (value?: string | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
};

// due date in ms for sorting; nulls sort last. For a recurring card the stored
// due_date is an ANCHOR, so sort by the next occurrence derived from it —
// otherwise every routine would pile up at the top forever as its anchor ages.
const dueMs = (t: Task): number => {
  const d = t.recurrence ? nextOccurrence(t.due_date, t.recurrence) : parseDate(t.due_date);
  return d ? d.getTime() : Number.POSITIVE_INFINITY;
};

// Completion timestamp for the 已完成 column (completed_at, else updated_at).
const completedTs = (t: Task): number => {
  const src = t.completed_at || t.updated_at;
  const d = src ? new Date(src) : null;
  return d && !isNaN(d.getTime()) ? d.getTime() : 0;
};

// Active-column ordering: priority (high→medium→low) then due date asc (nulls last).
// This ordering is the product — the top card of 待辦 is "the next thing to do".
const activeSort = (a: Task, b: Task): number => {
  const pr = (priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3);
  if (pr !== 0) return pr;
  return dueMs(a) - dueMs(b);
};

// Overdue = pending task whose due date is strictly before today (date-only).
// A recurring card can never be overdue: its next occurrence always rolls
// forward, so it reminds rather than accuses.
const isOverdue = (task: Task): boolean => {
  if (task.recurrence) return false;
  if (!task.due_date || task.status === 'completed') return false;
  const due = parseDate(task.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
};

interface TaskBoardViewProps {
  // Tasks already filtered by search/platform/status. Grouping is done here:
  // pending → active columns by section; completed → the derived 已完成 column.
  tasks: Task[];
  onOpenDetail: (task: Task) => void;
  onToggleStatus: (task: Task) => void;
  // Move to an active section. `claim` asks the backend to assign the caller.
  onMoveSection: (task: Task, section: string, opts?: { claim?: boolean }) => void;
  // Reopen a completed task, then move it to the given active section (sequential).
  onReopenTo: (task: Task, section: string) => void;
  onCreateInSection: (section: string) => void;
  onQuickAdd: (title: string) => void;
  // Remove a task outright (the parent confirms + calls the API).
  onDelete: (task: Task) => void;
  // Set or clear a card's recurrence rule ('' clears). `section` moves the card
  // to an active column in the same gesture — used when dragging out of 週期.
  onSetRecurrence: (task: Task, rule: string, section?: string) => void;
  // Needed to render every assignee's name: the task rows carry assignee_ids but
  // only the primary assignee's name is joined server-side.
  employees: Employee[];
}

const TaskBoardView: React.FC<TaskBoardViewProps> = ({
  tasks,
  onOpenDetail,
  onToggleStatus,
  onMoveSection,
  onReopenTo,
  onCreateInSection,
  onQuickAdd,
  onDelete,
  onSetRecurrence,
  employees,
}) => {
  // Display names for every assignee. assignee_ids is the source of truth;
  // assignee_name (the primary, joined server-side) covers rows fetched before
  // the multi-assignee column existed.
  const employeeNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employees) map.set(e.id, e.name);
    return map;
  }, [employees]);

  const assigneesOf = (task: Task): string[] => {
    const ids = task.assignee_ids;
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map((id) => employeeNames.get(id) ?? `#${id}`);
    }
    return task.assignee_name ? [task.assignee_name] : [];
  };

  // The card currently being dragged, and the column being hovered (drop target).
  const draggedTaskRef = useRef<Task | null>(null);
  const [dragOverSection, setDragOverSection] = useState<string | null>(null);
  // Quick-add input at the top of the 待辦 column.
  const [quickAddValue, setQuickAddValue] = useState<string>('');

  // Group tasks: pending by active section; completed into the derived column
  // (only last 7 days, newest first). Active columns are priority/due sorted.
  const grouped = useMemo(() => {
    const active: Record<string, Task[]> = { todo: [], doing: [], waiting: [], review: [] };
    const completedAll: Task[] = [];
    const recurring: Task[] = [];
    for (const task of tasks) {
      if (task.status === 'completed') {
        completedAll.push(task);
        continue;
      }
      // Recurring cards are pulled out before section grouping — the 週期 lane owns
      // them, so they never appear in 待辦/進行中 as well.
      if (task.recurrence) {
        recurring.push(task);
        continue;
      }
      active[sectionOf(task)].push(task);
    }
    for (const key of Object.keys(active)) active[key].sort(activeSort);

    // Routines sort by when they're next due — "what comes up first", not by
    // priority (a weekly chore's priority says little about when to do it).
    recurring.sort((a, b) => dueMs(a) - dueMs(b) || activeSort(a, b));

    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const completed = completedAll
      .filter((t) => completedTs(t) >= sevenDaysAgo)
      .sort((a, b) => completedTs(b) - completedTs(a));

    return {
      todo: active.todo,
      doing: active.doing,
      waiting: active.waiting,
      review: active.review,
      completed,
      recurring,
    };
  }, [tasks]);

  const handleDragStart = (e: React.DragEvent, task: Task): void => {
    draggedTaskRef.current = task;
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require data to be set for the drag to initiate.
    try {
      e.dataTransfer.setData('text/plain', String(task.id));
    } catch {
      /* ignore */
    }
  };

  const handleDragEnd = (): void => {
    draggedTaskRef.current = null;
    setDragOverSection(null);
  };

  const handleColumnDragOver = (e: React.DragEvent, sectionKey: string): void => {
    // preventDefault is required to allow a drop.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverSection !== sectionKey) setDragOverSection(sectionKey);
  };

  const handleColumnDrop = (e: React.DragEvent, sectionKey: string): void => {
    e.preventDefault();
    const task = draggedTaskRef.current;
    setDragOverSection(null);
    draggedTaskRef.current = null;
    if (!task) return;

    const fromCompleted = task.status === 'completed';
    const fromRecurring = !!task.recurrence;

    // Drop into 已完成 → mark completed (no section call). A recurring card takes
    // the same path: the server renews it instead of closing it, so it lands back
    // in 週期 with its next date.
    if (sectionKey === COMPLETED_KEY) {
      if (fromCompleted) return; // already completed, no-op
      onToggleStatus(task);
      return;
    }

    // Drop into 週期 → make it a routine. Default 每週; edit the card to change it.
    if (sectionKey === RECURRING_KEY) {
      if (fromRecurring) return; // already recurring, no-op
      if (fromCompleted) return; // reopen it first, then drag it in
      onSetRecurrence(task, DEFAULT_RECURRENCE);
      return;
    }

    // Drag OUT of 週期 into an active column → stop recurring, and land in that
    // column. Clearing the rule is what actually moves it out of the lane.
    if (fromRecurring) {
      onSetRecurrence(task, '', sectionKey);
      return;
    }

    // Drop into an active column from 已完成 → reopen then move (sequential).
    if (fromCompleted) {
      onReopenTo(task, sectionKey);
      return;
    }

    // Active → active. No-op when the column is unchanged.
    if (sectionOf(task) === sectionKey) return;

    // Pulling an unassigned task into 進行中 claims it for the caller.
    if (sectionKey === 'doing' && !task.assignee_id) {
      onMoveSection(task, sectionKey, { claim: true });
    } else {
      onMoveSection(task, sectionKey);
    }
  };

  const handleQuickAddKey = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const title = quickAddValue.trim();
    if (!title) return;
    onQuickAdd(title);
    setQuickAddValue(''); // clear; keep focus for rapid entry (input stays mounted)
  };

  // 週期 sits after 已完成 so the pull-based flow (待辦→進行中→…→已完成) reads
  // left-to-right unbroken, with the routines parked at the end.
  const columns: Array<{ key: string; label: string; completed: boolean }> = [
    ...BOARD_SECTIONS.map((s) => ({ ...s, completed: false })),
    { key: COMPLETED_KEY, label: '已完成', completed: true },
    { key: RECURRING_KEY, label: '週期', completed: false },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        alignItems: 'flex-start',
        pb: 1,
      }}
    >
      {columns.map((column) => {
        const columnTasks = (grouped as Record<string, Task[]>)[column.key] || [];
        const isDropTarget = dragOverSection === column.key;
        const isCompletedColumn = column.completed;
        return (
          <Box
            key={column.key}
            onDragOver={(e) => handleColumnDragOver(e, column.key)}
            onDragLeave={(e) => {
              // Only clear when leaving the column entirely, not when moving over a child.
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOverSection((prev) => (prev === column.key ? null : prev));
              }
            }}
            onDrop={(e) => handleColumnDrop(e, column.key)}
            sx={{
              flex: '0 0 300px',
              width: 300,
              maxWidth: 300,
              bgcolor: isDropTarget ? 'action.hover' : 'grey.50',
              border: 1,
              borderColor: isDropTarget ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 1.5,
              transition: 'background-color 0.15s ease, border-color 0.15s ease',
            }}
          >
            {/* Column header with live count */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5, px: 0.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700}>
                  {column.label}
                </Typography>
                <Chip label={columnTasks.length} size="small" />
              </Stack>
            </Stack>

            {/* Quick-add row — only at the top of 待辦. Enter creates immediately. */}
            {column.key === 'todo' && (
              <TextField
                fullWidth
                size="small"
                placeholder="快速新增…"
                value={quickAddValue}
                onChange={(e) => setQuickAddValue(e.target.value)}
                onKeyDown={handleQuickAddKey}
                sx={{ mb: 1, bgcolor: 'background.paper' }}
              />
            )}

            {/* Cards */}
            <Stack spacing={1}>
              {columnTasks.map((task) => {
                const cardCompleted = task.status === 'completed';
                return (
                  <Card
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onOpenDetail(task)}
                    sx={{
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      '&:hover': { boxShadow: 3 },
                      borderLeft: 4,
                      borderLeftColor: priorityBorderColors[task.priority] || 'grey.400',
                      opacity: cardCompleted ? 0.6 : 1,
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        {/* Complete checkbox — stops propagation so it never opens the
                            drawer or starts a drag. */}
                        <Box
                          draggable={false}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDragStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={cardCompleted}
                            onChange={() => onToggleStatus(task)}
                            color="success"
                            sx={{ p: 0.25, mt: -0.25 }}
                          />
                        </Box>

                        {/* Delete — flex order pushes it to the row's right edge;
                            same propagation shielding as the checkbox. */}
                        <Box
                          draggable={false}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDragStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          sx={{ order: 2 }}
                        >
                          <IconButton
                            size="small"
                            aria-label="刪除任務"
                            onClick={() => onDelete(task)}
                            sx={{ p: 0.25, mt: -0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              mb: 0.75,
                              textDecoration: cardCompleted ? 'line-through' : 'none',
                              color: cardCompleted ? 'text.secondary' : 'text.primary',
                            }}
                          >
                            {task.title}
                          </Typography>

                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            {/* Priority chip */}
                            <Chip
                              label={priorityLabels[task.priority] || task.priority}
                              size="small"
                              color={priorityColors[task.priority] || 'default'}
                            />

                            {/* Due date. For a recurring card this is the NEXT
                                occurrence, derived from the anchor — it rolls on
                                its own, so the reminder is never stale and never
                                needs anyone to press 完成. Highlighted amber on
                                the day it lands. */}
                            {task.due_date && (() => {
                              const next = task.recurrence
                                ? nextOccurrence(task.due_date, task.recurrence)
                                : parseDate(task.due_date);
                              if (!next) return null;
                              const inDays = daysUntil(next);
                              const dueNow = task.recurrence && inDays !== null && inDays <= 0;
                              return (
                                <Chip
                                  icon={<EventIcon />}
                                  label={
                                    task.recurrence
                                      ? dueNow
                                        ? '今天該做'
                                        : `下次 ${next.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })}`
                                      : formatDueDate(task.due_date)
                                  }
                                  size="small"
                                  color={isOverdue(task) ? 'error' : dueNow ? 'warning' : 'default'}
                                  variant={isOverdue(task) || dueNow ? 'filled' : 'outlined'}
                                />
                              );
                            })()}

                            {/* 週期任務 marker. The card itself recurs — completing it
                                reschedules it rather than closing it — so the chip
                                tells whoever picks it up that this one comes back. */}
                            {task.recurrence && (
                              <Tooltip title="週期任務：完成後會自動排下一次，卡片留在看板上">
                                <Chip
                                  icon={<RepeatIcon />}
                                  label={recurrenceLabel(task.recurrence)}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}

                            {/* Assignees: one chip each; 可認領 only when nobody owns it. */}
                            {assigneesOf(task).length > 0 ? (
                              assigneesOf(task).map((name, i) => (
                                <Chip
                                  key={`${task.id}-a${i}`}
                                  size="small"
                                  variant="outlined"
                                  avatar={<Avatar>{name.charAt(0)}</Avatar>}
                                  label={name}
                                />
                              ))
                            ) : (
                              !cardCompleted && (
                                <Tooltip title="尚未指派，拖到進行中即可認領">
                                  <Chip label="可認領" size="small" variant="outlined" color="info" />
                                </Tooltip>
                              )
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Per-column create button — active columns only (none on 已完成). */}
              {!isCompletedColumn && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onCreateInSection(column.key)}
                  sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
                >
                  新增任務
                </Button>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default TaskBoardView;
